import { supabase } from "./supabase";
import { attributionCarryParams } from "./attribution";

// Same localStorage key the email signup writes (signup/page.tsx) so the
// /welcome step reads + clears one segment value regardless of which path
// the user took. Kept in sync with SEGMENT_COPY's keys on the signup page.
const SEGMENT_KEY = "maren_signup_segment";
const KNOWN_SEGMENTS = new Set(["retaker", "first_time", "non_trad", "plateau"]);

export interface GoogleSignInContext {
  segment?: string | null;
  plan?: string | null;
  next?: string | null;
}

/**
 * Start the Google OAuth (implicit-grant) flow.
 *
 * The homepage segment can't survive the round-trip in a query param the
 * way the email form keeps it, so we persist it to localStorage before
 * redirecting — the same key + the same four allowed segments as the email
 * path. The paid plan and a post-login `next` are passed through the
 * callback URL so /auth/callback can route the user onward.
 *
 * On success the browser navigates to Google and this never resolves for
 * the caller; on failure it returns the supabase error so the button can
 * surface it and fall back to the email form.
 */
export async function signInWithGoogle({ segment, plan, next }: GoogleSignInContext = {}) {
  if (segment && KNOWN_SEGMENTS.has(segment)) {
    try {
      localStorage.setItem(SEGMENT_KEY, segment);
    } catch {
      // localStorage can throw in privacy modes; harmless to skip.
    }
  }

  const params = new URLSearchParams();
  if (plan === "pro" || plan === "max") params.set("plan", plan);
  // Only carry a safe relative path — never an absolute/protocol URL.
  if (next && next.startsWith("/") && !next.startsWith("//")) params.set("next", next);

  // Carry the acquisition source across the round trip. The visitor can
  // come back in a DIFFERENT storage context than the one that captured
  // the original touch, which is the normal case when a social in-app
  // browser hands sign-in off to the system browser. Without this the
  // channel is lost precisely for the traffic we most want to measure,
  // and the visitor gets recorded against whatever referrer the provider
  // happened to send.
  for (const [k, v] of attributionCarryParams()) params.set(k, v);

  const query = params.toString();
  const redirectTo = `${window.location.origin}/auth/callback${query ? `?${query}` : ""}`;

  return supabase.auth.signInWithOAuth({
    provider: "google",
    options: {
      redirectTo,
      // Always show the account chooser so a student with multiple Google
      // accounts isn't silently signed into the wrong one.
      queryParams: { prompt: "select_account" },
    },
  });
}
